import { useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { Input, Label, Textarea } from '@/components/ui/Input';

interface CreateResp { data: { id: number | string } }

export const Route = createFileRoute('/_app/forms/new')({
  component: NewFormPage,
});

function NewFormPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [title, setTitle] = useState('');
  const [key, setKey] = useState('');
  const [description, setDescription] = useState('');

  const create = useMutation({
    mutationFn: () =>
      api.post<CreateResp>('/forms', {
        key: key || slugify(title),
        title,
        description: description || null,
        fields: [
          { name: 'nombre', label: 'Nombre', type: 'text', required: true, width: 'full' },
          { name: 'email', label: 'Email', type: 'email', required: true, width: 'full' },
          { name: 'mensaje', label: 'Mensaje', type: 'textarea', required: false, width: 'full' },
        ],
        success_message: 'Gracias, te contactaremos pronto.',
        honeypot_enabled: true,
        store_submissions: true,
        is_active: true,
      }),
    onSuccess: (res) => {
      const id = res.data.data.id;
      toast.success('Formulario creado');
      void qc.invalidateQueries({ queryKey: ['forms'] });
      void navigate({ to: '/forms/$id', params: { id: String(id) } });
    },
    onError: () => toast.error('No se pudo crear (¿clave ya en uso?)'),
  });

  return (
    <div className="mx-auto max-w-xl space-y-6 p-6 md:p-8">
      <Link
        to="/forms"
        className="inline-flex items-center gap-1 text-xs text-neutral-500 hover:text-neutral-900"
      >
        <ArrowLeft className="size-3" /> Formularios
      </Link>
      <Card>
        <CardHeader>
          <CardTitle>Nuevo formulario</CardTitle>
          <CardDescription>Configura los datos básicos. Después podrás añadir campos visualmente.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1">
            <Label>Título</Label>
            <Input
              value={title}
              autoFocus
              onChange={(e) => {
                setTitle(e.target.value);
                if (!key) setKey(slugify(e.target.value));
              }}
              placeholder="Formulario de contacto"
            />
          </div>
          <div className="space-y-1">
            <Label>Clave técnica</Label>
            <Input
              value={key}
              onChange={(e) => setKey(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '-'))}
              placeholder="contacto"
            />
            <p className="text-[10px] text-neutral-400">Se usará en el bloque <code>form</code>.</p>
          </div>
          <div className="space-y-1">
            <Label>Descripción (opcional)</Label>
            <Textarea
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" asChild>
              <Link to="/forms">Cancelar</Link>
            </Button>
            <Button
              disabled={!title.trim() || create.isPending}
              onClick={() => create.mutate()}
            >
              {create.isPending ? 'Creando…' : 'Crear y empezar'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function slugify(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
}
